/**
 * Stable error codes thrown by the prospect create/update handler chains
 * (`CODE: human message`, precedent `origin-groups.service.ts:84`).
 * `PROSPECT_ERROR_I18N` is the single source that maps each code to its
 * i18n key — without it the code (in the handler) and the map key
 * (in `backend-error-map.ts`) would be two hand-synced literals.
 */
export const PROSPECT_ERROR_CODE = {
  NO_DEFAULT_STAGE: 'PROSPECT_NO_DEFAULT_STAGE',
  GROUP_NOT_FOUND: 'PROSPECT_GROUP_NOT_FOUND',
  PROJECT_NOT_FOUND: 'PROSPECT_PROJECT_NOT_FOUND',
  ORIGIN_NOT_FOUND: 'PROSPECT_ORIGIN_NOT_FOUND',
  ASSIGNED_USER_NOT_FOUND: 'PROSPECT_ASSIGNED_USER_NOT_FOUND',
  STAGE_NOT_FOUND: 'PROSPECT_STAGE_NOT_FOUND',
  STAGE_DISABLED: 'PROSPECT_STAGE_DISABLED',
  OBJECTION_NOT_FOUND: 'PROSPECT_OBJECTION_NOT_FOUND',
  PHONE_ALREADY_EXISTS: 'PROSPECT_PHONE_ALREADY_EXISTS',
  INVALID_PHONE_FORMAT: 'PROSPECT_INVALID_PHONE_FORMAT',
  SUPERADMIN_CANNOT_CREATE: 'PROSPECT_SUPERADMIN_CANNOT_CREATE',
  NO_AD_ID: 'PROSPECT_NO_AD_ID',
  AD_PREVIEW_CONFIG_MISSING: 'PROSPECT_AD_PREVIEW_CONFIG_MISSING',
  HISTORY_ENTRY_IS_CURRENT: 'PROSPECT_HISTORY_ENTRY_IS_CURRENT',
  HISTORY_ENTRY_IS_ORIGIN: 'PROSPECT_HISTORY_ENTRY_IS_ORIGIN',
  HISTORY_ENTRY_ACTIVE_APPOINTMENT: 'PROSPECT_HISTORY_ENTRY_ACTIVE_APPOINTMENT',
  HISTORY_ENTRY_REVIVES_APPOINTMENT: 'PROSPECT_HISTORY_ENTRY_REVIVES_APPOINTMENT',
  EXTRA_FIELD_NOT_CONFIGURED: 'PROSPECT_EXTRA_FIELD_NOT_CONFIGURED',
} as const;

export type ProspectErrorCode = (typeof PROSPECT_ERROR_CODE)[keyof typeof PROSPECT_ERROR_CODE];

export const PROSPECT_ERROR_I18N: Record<ProspectErrorCode, string> = {
  [PROSPECT_ERROR_CODE.NO_DEFAULT_STAGE]: 'errors.prospectNoDefaultStage',
  [PROSPECT_ERROR_CODE.GROUP_NOT_FOUND]: 'errors.prospectGroupNotFound',
  [PROSPECT_ERROR_CODE.PROJECT_NOT_FOUND]: 'errors.prospectProjectNotFound',
  [PROSPECT_ERROR_CODE.ORIGIN_NOT_FOUND]: 'errors.prospectOriginNotFound',
  [PROSPECT_ERROR_CODE.ASSIGNED_USER_NOT_FOUND]: 'errors.prospectAssignedUserNotFound',
  [PROSPECT_ERROR_CODE.STAGE_NOT_FOUND]: 'errors.prospectStageNotFound',
  [PROSPECT_ERROR_CODE.STAGE_DISABLED]: 'errors.prospectStageDisabled',
  [PROSPECT_ERROR_CODE.OBJECTION_NOT_FOUND]: 'errors.prospectObjectionNotFound',
  // Reuses the EXISTING key (already handled via the legacy substring match
  // below) — no new i18n key for this one.
  [PROSPECT_ERROR_CODE.PHONE_ALREADY_EXISTS]: 'errors.phoneAlreadyExists',
  [PROSPECT_ERROR_CODE.INVALID_PHONE_FORMAT]: 'errors.prospectInvalidPhoneFormat',
  [PROSPECT_ERROR_CODE.SUPERADMIN_CANNOT_CREATE]: 'errors.prospectSuperAdminCannotCreate',
  [PROSPECT_ERROR_CODE.NO_AD_ID]: 'errors.prospectNoAdId',
  [PROSPECT_ERROR_CODE.AD_PREVIEW_CONFIG_MISSING]: 'errors.prospectAdPreviewConfigMissing',
  [PROSPECT_ERROR_CODE.HISTORY_ENTRY_IS_CURRENT]: 'errors.prospectHistoryEntryIsCurrent',
  [PROSPECT_ERROR_CODE.HISTORY_ENTRY_IS_ORIGIN]: 'errors.prospectHistoryEntryIsOrigin',
  [PROSPECT_ERROR_CODE.HISTORY_ENTRY_ACTIVE_APPOINTMENT]: 'errors.prospectHistoryEntryActiveAppointment',
  [PROSPECT_ERROR_CODE.HISTORY_ENTRY_REVIVES_APPOINTMENT]: 'errors.prospectHistoryEntryRevivesAppointment',
  [PROSPECT_ERROR_CODE.EXTRA_FIELD_NOT_CONFIGURED]: 'errors.prospectExtraFieldNotConfigured',
};
